import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Minimal anchored popover. Closes on outside click and Escape, returns focus
 * to the trigger. If the app already has a Popover (e.g. shadcn/Radix), swap
 * this for it — the props are intentionally simple.
 *
 * Rendered via a `fixed`-position portal into document.body, computed from
 * the trigger's own rect — same approach as FloatingTooltip, so it can never
 * get clipped by a scrollable ancestor (e.g. the filter sidebar's own
 * internal scroll), and it flips above the trigger when there isn't enough
 * room below.
 */
export default function Popover({ open, onOpenChange, trigger, children, align = 'start', width = 280 }) {
  const triggerRef = useRef(null);
  const outerRef = useRef(null); // the visible, clamped+scrollable popover box
  const measureRef = useRef(null); // inner wrapper, unclamped — used only to read the content's real height
  const roRef = useRef(null);
  const id = useId();
  const [pos, setPos] = useState(null);
  const MARGIN = 10;
  const OUTER_CHROME = 26; // outer box's own p-3 (12px * 2) + border (1px * 2), not part of the measured inner wrapper

  // Kept in a ref (not re-created per render) so both the scroll/resize
  // listeners below and the ResizeObserver callback (attached via the
  // content callback-ref, before this effect's cleanup would otherwise
  // run) always call the latest version.
  const updateRef = useRef(() => {});
  updateRef.current = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // measureRef is the unclamped inner wrapper, so this is the content's
    // real, natural height — using the capped outer box here would make
    // the measurement equal to whatever cap was last applied, so the flip
    // decision below could never detect "there's actually more room
    // above". The outer box also adds its own padding (p-3 = 12px * 2) and
    // border (1px * 2) around that content, which the inner wrapper itself
    // doesn't include — add it back so the comparison matches what will
    // actually be rendered.
    const innerH = measureRef.current?.offsetHeight;
    const contentH = innerH != null ? innerH + OUTER_CHROME : 220; // 220 = best guess (already "outer-sized") before first paint
    const roomBelow = window.innerHeight - r.bottom - MARGIN;
    const roomAbove = r.top - MARGIN;
    const openUp = roomBelow < contentH && roomAbove > roomBelow;
    // Bound the popover to whatever room it actually has in the direction
    // it opens, with its own scroll — rather than letting tall content
    // (e.g. the services list after "Browse all") render past the
    // viewport edge uncapped.
    const maxHeight = Math.max(openUp ? roomAbove : roomBelow, 120);
    const rawLeft = align === 'end' ? r.right - width : r.left;
    const left = Math.min(Math.max(rawLeft, 8), window.innerWidth - width - 8);
    setPos({ left, openUp, maxHeight, triggerTop: r.top, triggerBottom: r.bottom });
  };

  useEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const update = () => updateRef.current();
    update();
    // `true` = capture phase, so this also fires for scrolls on an ancestor
    // scroll container (e.g. the sidebar's own overflow-y-auto), not just
    // the window — scroll events don't bubble, but capture-phase listeners
    // still see them on the way down to the target.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, align, width]);

  // Callback ref (fires the instant the measuring wrapper mounts/unmounts)
  // so we can re-measure its real height as soon as it exists — the first
  // `update()` above runs before the portal has painted, so it only has
  // the 220px guess — and keep re-measuring if the content's own size
  // changes later (e.g. a list that grows after "Browse all").
  // Memoized: an inline callback ref is a new function every render, so
  // React would detach/re-attach it on each render -> updateRef -> setPos
  // -> re-render -> infinite loop ("Maximum update depth exceeded").
  const setMeasureRef = useCallback((node) => {
    measureRef.current = node;
    roRef.current?.disconnect();
    roRef.current = null;
    if (node && typeof ResizeObserver !== 'undefined') {
      roRef.current = new ResizeObserver(() => updateRef.current());
      roRef.current.observe(node);
    }
    if (node) updateRef.current();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (outerRef.current?.contains(e.target)) return;
      // A modal dialog (e.g. Radix Dialog, used by "Browse all services")
      // portals straight to document.body, so DOM-wise it sits outside
      // this popover's own subtree — without this check, opening one from
      // inside the popover closes the popover on the dialog's very first
      // click (treated as "outside"), which unmounts the popover's content
      // and takes the dialog down with it before anything can be applied.
      if (e.target.closest('[role="dialog"]')) return;
      onOpenChange(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <>
      {trigger({
        ref: triggerRef,
        'aria-expanded': open,
        'aria-controls': id,
        'aria-haspopup': 'dialog',
        onClick: () => onOpenChange(!open),
      })}
      {open &&
        pos &&
        createPortal(
          <div
            ref={outerRef}
            id={id}
            role="dialog"
            style={{
              position: 'fixed',
              left: pos.left,
              width,
              maxHeight: pos.maxHeight,
              overflowY: 'auto',
              ...(pos.openUp ? { bottom: window.innerHeight - pos.triggerTop + 8 } : { top: pos.triggerBottom + 8 }),
            }}
            className="z-[999] rounded-[14px] border border-sr-stroke bg-sr-panel p-3 shadow-[0_18px_50px_rgba(0,0,0,.65)]"
          >
            <div ref={setMeasureRef}>{children}</div>
          </div>,
          document.body,
        )}
    </>
  );
}
